<template>
  <!--
    Ordens de produção da Box para o residente.
    Pergunta que a tela responde: "a farmácia já produziu o pouch deste residente?"
  -->
  <section class="bxp" aria-labelledby="bxp-title">
    <header class="bxp-head">
      <div>
        <h2 id="bxp-title" class="bxp-head__title">Ordens de produção</h2>
        <p class="bxp-head__subtitle">
          Situação da produção dos pouches na farmácia.
        </p>
      </div>
      <button
        type="button"
        class="btn-modern btn-modern-save"
        :disabled="loading"
        @click="load"
      >
        <i
          :class="[
            'fas',
            loading ? 'fa-spinner fa-spin' : 'fa-sync-alt',
            'mr-2',
          ]"
          aria-hidden="true"
        ></i>
        {{ loading ? "Consultando…" : "Atualizar" }}
      </button>
    </header>

    <!-- Filtros: só os que mudam a decisão (Lei de Hick) -->
    <form class="bxp-filters" @submit.prevent="load">
      <div class="bxp-field">
        <label for="bxp-status">Situação</label>
        <select
          id="bxp-status"
          v-model="filters.status_id"
          class="form-control"
        >
          <option value="">Todas</option>
          <option v-for="s in statuses" :key="s.id" :value="s.id">
            {{ s.label }}
          </option>
        </select>
      </div>
      <div class="bxp-field">
        <label for="bxp-from">De</label>
        <input
          id="bxp-from"
          v-model="filters.data_inicio"
          type="date"
          class="form-control"
        />
      </div>
      <div class="bxp-field">
        <label for="bxp-to">Até</label>
        <input
          id="bxp-to"
          v-model="filters.data_fim"
          type="date"
          class="form-control"
        />
      </div>
      <button type="submit" class="bxp-apply" :disabled="loading">
        Filtrar
      </button>
    </form>

    <div
      v-if="error"
      class="bxp-alert"
      :class="offline ? 'bxp-alert--warning' : 'bxp-alert--danger'"
      role="alert"
    >
      <i class="fas fa-exclamation-triangle mr-2" aria-hidden="true"></i>
      <span>
        {{ error }}
        <template v-if="offline">
          Mostrando a última cópia salva no SistemaClin.</template
        >
      </span>
    </div>

    <p v-if="loading && !items.length" class="bxp-empty" role="status">
      Consultando a Box…
    </p>

    <p v-else-if="!items.length && !error" class="bxp-empty" role="status">
      Nenhuma ordem de produção para este residente no período.
    </p>

    <div v-else-if="items.length" class="bxp-table-wrap">
      <table class="bxp-table">
        <caption class="sr-only">
          Ordens de produção do residente
        </caption>
        <thead>
          <tr>
            <th scope="col">Ordem</th>
            <th scope="col">Situação</th>
            <th scope="col">Período</th>
            <th scope="col">Produção</th>
            <th scope="col">Itens</th>
            <th scope="col">Atualizado</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in items" :key="o.id">
            <th scope="row">#{{ o.codigo || o.id }}</th>
            <td>
              <span class="bxp-pill" :class="`bxp-pill--${tone(o)}`">
                <i :class="['fas', icon(o)]" aria-hidden="true"></i>
                {{ o.status_label || o.status || "—" }}
              </span>
            </td>
            <td>{{ period(o) }}</td>
            <td>{{ date(o.data_producao) || "—" }}</td>
            <td>{{ o.itens != null ? o.itens : "—" }}</td>
            <td>{{ date(o.atualizado_em) || "—" }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import api from "../../../services/api";
import { formatDate } from "../../../lib/format";

export default {
  name: "BoxProductionOrders",
  props: {
    box_id: [String, Number],
    resident_id: [String, Number],
  },
  data() {
    return {
      items: [],
      statuses: [],
      filters: { status_id: "", data_inicio: "", data_fim: "" },
      loading: false,
      error: null,
      offline: false,
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      this.offline = false;
      try {
        const { data } = await api.get("/rest-home/box/production-orders", {
          params: {
            box_id: this.box_id,
            resident_id: this.resident_id,
            ...this.filters,
          },
        });
        this.items = data.items || [];
        if (data.statuses && data.statuses.length)
          this.statuses = data.statuses;
        if (data.error) {
          this.error = (data.errors || []).join(" ") || "A Box não respondeu.";
          this.offline = !!data.offline;
        }
      } catch (error) {
        this.error = "Falha de comunicação com o servidor.";
      } finally {
        this.loading = false;
      }
    },

    date(value) {
      // A Box devolve ISO em UTC; só a data vale
      return value ? formatDate(String(value).substring(0, 10), false) : "";
    },

    period(o) {
      if (!o.data_inicio && !o.data_fim) return "—";
      return `${this.date(o.data_inicio) || "?"} a ${
        this.date(o.data_fim) || "?"
      }`;
    },

    /** Cor pelo nome interno do status (os ids mudam por ambiente). */
    tone(o) {
      const s = String(o.status || "").toUpperCase();
      if (/CANCEL|ERRO|RECUS/.test(s)) return "danger";
      if (/CONCLU|FINALIZ|PRODUZID|ENTREG/.test(s)) return "success";
      if (/PRODU|ANDAMENT|SEPARA|CONFER/.test(s)) return "info";
      return "neutral";
    },

    icon(o) {
      return {
        danger: "fa-times-circle",
        success: "fa-check-circle",
        info: "fa-cog",
        neutral: "fa-clock",
      }[this.tone(o)];
    },
  },
};
</script>

<style scoped>
.bxp {
  color: #1e293b;
}
.bxp-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}
.bxp-head__title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
}
.bxp-head__subtitle {
  color: #475569;
  margin: 0;
}

.bxp-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
}
.bxp-field {
  display: flex;
  flex-direction: column;
  min-width: 180px;
}
.bxp-field label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}
.bxp-apply {
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid #0f65b0;
  border-radius: 8px;
  background: #fff;
  color: #0f65b0;
  font-weight: 600;
  cursor: pointer;
}

.bxp-alert {
  display: flex;
  align-items: flex-start;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.bxp-alert--danger {
  background: #fef2f2;
  color: #b91c1c;
}
.bxp-alert--warning {
  background: #fffbeb;
  color: #92400e;
}

.bxp-empty {
  padding: 32px 16px;
  text-align: center;
  color: #475569;
  background: #f8fafc;
  border-radius: 12px;
}

.bxp-table-wrap {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.bxp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}
.bxp-table th,
.bxp-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  white-space: nowrap;
}
.bxp-table thead th {
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.bxp-table tbody tr:hover {
  background: #f8fafc;
}

.bxp-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
}
.bxp-pill--success {
  background: #ecfdf5;
  color: #047857;
}
.bxp-pill--info {
  background: #e0f2fe;
  color: #075985;
}
.bxp-pill--danger {
  background: #fef2f2;
  color: #b91c1c;
}
.bxp-pill--neutral {
  background: #f1f5f9;
  color: #475569;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
.bxp button:focus-visible,
.bxp select:focus-visible,
.bxp input:focus-visible {
  outline: 3px solid #f59e0b;
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .bxp-head {
    flex-direction: column;
  }
  .bxp-field {
    min-width: 100%;
  }
}
</style>
